import type { DesktopRoute } from "@zentrack/config";
import type {
  ActiveSession,
  AppUsage,
  Client,
  ClientReportRow,
  DailyReportRow,
  HistoryDaySection,
  InstalledApplicationOption,
  Project,
  ProjectSoftwareRule,
  TimeEntry,
} from "@zentrack/domain";

export type TimelineViewMode = "calendar" | "list";

export interface AppSettings {
  lastRoute: DesktopRoute;
  autoPauseOnIdle: boolean;
  idleThresholdMinutes: number;
  /** Timeline route view toggle. Optional: settings saved before the calendar existed omit it (treat as "calendar"). */
  timelineView?: TimelineViewMode;
}

export interface CreateClientInput {
  name: string;
}

export interface UpdateClientInput {
  id: string;
  name: string;
}

export interface RemoveClientInput {
  id: string;
}

export interface CreateProjectInput {
  clientId: string;
  name: string;
  /** Ramp token ("chart-1"…) or raw OKLCH. Omitted → auto-assigned by cycling the ramp. */
  color?: string;
  softwareRules?: ProjectSoftwareRule[];
}

export interface UpdateProjectInput {
  id: string;
  clientId: string;
  name: string;
  /** Omitted → keep the stored color. */
  color?: string;
  softwareRules?: ProjectSoftwareRule[];
}

export interface RemoveProjectInput {
  id: string;
}

export interface StartTimerInput {
  projectId: string;
  note: string;
}

export interface CreateManualEntryInput {
  projectId: string;
  note: string;
  startTime: string;
  endTime: string;
}

export interface UpdateManualEntryInput {
  id: string;
  projectId: string;
  note: string;
  startTime: string;
  endTime: string;
}

export interface DeleteTimeEntryInput {
  id: string;
}

export interface ExportCsvInput {
  /**
   * Optional filename hint for browser-mode Blob downloads. The Electron main
   * process intentionally ignores this value: the export destination is always
   * chosen through the native save dialog, so a renderer can never write the
   * CSV to an arbitrary path.
   */
  destinationPath?: string;
}

export interface ExportCsvResult {
  filePath: string;
  fileName: string;
  rowCount: number;
}

export const MENU_ACTION_EVENT = "zentrack://menu-action";
export const MENU_ACTION_EXPORT_CSV = "app.file.export-csv";
export const MENU_ACTION_CHECK_FOR_UPDATES = "app.updater.check-for-updates";

export type DesktopMenuActionId =
  | typeof MENU_ACTION_EXPORT_CSV
  | typeof MENU_ACTION_CHECK_FOR_UPDATES;

export interface DesktopMenuActionPayload {
  actionId: DesktopMenuActionId;
}

export interface AppInfo {
  name: string;
  version: string;
}

export interface ReportsSnapshot {
  totalTrackedDurationSeconds: number;
  trackedClientCount: number;
  trackedProjectCount: number;
  hasFilteredSessions: boolean;
  dailyRows: DailyReportRow[];
  clientRows: ClientReportRow[];
}

export interface TraySnapshot {
  activeSession: ActiveSession | null;
  projectLabel: string | null;
  elapsedSeconds: number;
  canStopTimer: boolean;
}

export interface TrackingCapability {
  platform: "macos" | "unknown";
  status: "supported" | "degraded" | "unsupported";
  message: string | null;
  supportsInstalledApplications: boolean;
  supportsForegroundTracking: boolean;
  nativeIdleDetection: boolean;
}

export interface DesktopSnapshot {
  appInfo: AppInfo;
  clients: Client[];
  projects: Project[];
  projectSoftwareRules: ProjectSoftwareRule[];
  timeEntries: TimeEntry[];
  appUsages: AppUsage[];
  activeSession: ActiveSession | null;
  settings: AppSettings;
  tray: TraySnapshot;
  reports: ReportsSnapshot;
  trackingCapability: TrackingCapability;
}

export const SNAPSHOT_INVALIDATED_EVENT = "zentrack://snapshot-invalidated";
export const DESKTOP_ACTION_ERROR_EVENT = "zentrack://action-error";

export interface DesktopApi {
  bootstrap(): Promise<DesktopSnapshot>;
  clients: {
    list(): Promise<Client[]>;
    create(input: CreateClientInput): Promise<Client>;
    update(input: UpdateClientInput): Promise<Client>;
    remove(input: RemoveClientInput): Promise<void>;
  };
  projects: {
    list(): Promise<Project[]>;
    listSoftwareRules(): Promise<ProjectSoftwareRule[]>;
    create(input: CreateProjectInput): Promise<Project>;
    update(input: UpdateProjectInput): Promise<Project>;
    remove(input: RemoveProjectInput): Promise<void>;
  };
  timer: {
    start(input: StartTimerInput): Promise<ActiveSession>;
    pause(): Promise<ActiveSession>;
    resume(): Promise<ActiveSession>;
    stop(): Promise<TimeEntry>;
    getCurrentSession(): Promise<ActiveSession | null>;
  };
  history: {
    listDays(): Promise<HistoryDaySection[]>;
    createManualEntry(input: CreateManualEntryInput): Promise<TimeEntry>;
    updateManualEntry(input: UpdateManualEntryInput): Promise<TimeEntry>;
    deleteEntry(input: DeleteTimeEntryInput): Promise<void>;
  };
  reports: {
    getDashboard(): Promise<ReportsSnapshot>;
    exportCsv(input?: ExportCsvInput): Promise<ExportCsvResult | null>;
  };
  settings: {
    get(): Promise<AppSettings>;
    update(input: Partial<AppSettings>): Promise<AppSettings>;
  };
  window: {
    showMain(): Promise<void>;
  };
  tray: {
    getState(): Promise<TraySnapshot>;
  };
  tracking: {
    getCapability(): Promise<TrackingCapability>;
    getInstalledApplications(): Promise<InstalledApplicationOption[]>;
    refreshInstalledApplications(): Promise<InstalledApplicationOption[]>;
  };
}

export class DesktopApiError extends Error {
  readonly code: string;

  constructor(code: string, message: string) {
    super(message);
    this.name = "DesktopApiError";
    this.code = code;
  }
}
